import { useEffect } from 'react';

import { useMaplibreMap } from '@App/components/MaplibreMap/maplibre-map.context';
import { useMapPopup, useLayerHover } from '@App/components/MaplibreMap/maplibre-map.hooks';
import { removeLayerAndSource } from '@App/components/MaplibreMap/maplibre-map.utils';
import { useContaminants } from '@services/query.service';
import TranslationsProvider from '@utils/i18n/i18n';
import translator from '@App/app.translator';
import theme from '@style/theme.json';

import MicrobialContaminationPopup from './MicrobialContaminationPopup/MicrobialContaminationPopup';

const SOURCE_ID = 'microbial-contamination';
const LAYER_ID = 'microbial-contamination-points';

function MicrobialContaminationLayer() {
  const { map, isLoading } = useMaplibreMap();
  const { data: contaminants } = useContaminants();
  const { openPopup, closePopup } = useMapPopup();

  useLayerHover(map, LAYER_ID);

  useEffect(() => {
    if (!map || isLoading) return undefined;

    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });

    map.addLayer({
      id: LAYER_ID,
      type: 'circle',
      source: SOURCE_ID,
      paint: {
        'circle-color': theme.color.primary.main,
        'circle-radius': 7,
        'circle-stroke-color': '#FFFFFF',
        'circle-stroke-width': 2,
      },
    }, 'z-index-5');

    return () => {
      closePopup();
      removeLayerAndSource(map, LAYER_ID, SOURCE_ID);
    };
  }, [isLoading, map, openPopup, closePopup]);

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const handleClick = (e) => {
      if (!e.features?.[0]) return;

      const feature = contaminants?.find(f => String(f.properties.id) === String(e.features[0].properties.id));

      if (!feature) return;

      openPopup(
        map,
        e.lngLat,
        <TranslationsProvider translator={translator}>
          <MicrobialContaminationPopup feature={feature} />
        </TranslationsProvider>,
      );
    };

    map.on('click', LAYER_ID, handleClick);

    return () => {
      map?.off('click', LAYER_ID, handleClick);
    };
  }, [map, contaminants, openPopup, closePopup]);

  useEffect(() => {
    if (isLoading || !contaminants || !map?.getSource(SOURCE_ID)) return;

    map.getSource(SOURCE_ID).setData({
      type: 'FeatureCollection',
      features: contaminants,
    });
  }, [isLoading, contaminants, map]);

  return null;
}

export default MicrobialContaminationLayer;
